import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Tooltip,
  Typography,
  Row,
  Col,
  Card,
  Popconfirm,
  message,
  Input,
  Select,
  Alert
} from 'antd';
import { 
  DisconnectOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  ReloadOutlined,
  WarningOutlined,
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { Option } = Select;

// ✅ ARREGLADO: Cambié props para coincidir con UsuariosAdminPage
const SesionesActivasModal = ({ visible, onCancel, onSessionClosed }) => {
  const [loading, setLoading] = useState(false);
  const [sesiones, setSesiones] = useState([]);
  const [filtros, setFiltros] = useState({
    search: '',
    usuario_id: ''
  });
  const [usuarios, setUsuarios] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  
  const { api, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  useEffect(() => {
    if (visible) {
      cargarSesiones();
      cargarUsuarios();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      cargarSesiones();
    }
  }, [filtros]);

  const cargarUsuarios = async () => {
    try {
      const response = await api.get('/api/usuarios-admin', {
        params: { limit: 1000 }
      });
      // ✅ ARREGLADO: Ajustado para estructura de datos del backend
      setUsuarios(response.data?.data || []);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  };

  const cargarSesiones = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/usuarios-admin/sesiones-activas', {
        params: filtros
      });
      // ✅ ARREGLADO: Ajustado para estructura de datos del backend
      setSesiones(response.data?.data || []);
    } catch (error) {
      console.error('Error cargando sesiones:', error);
      message.error('Error al cargar las sesiones activas');
    } finally {
      setLoading(false);
    }
  };

  const cerrarSesion = async (sesionId) => {
    try {
      await api.post(`/api/usuarios-admin/sesiones/${sesionId}/cerrar`);
      message.success('Sesión cerrada exitosamente');
      cargarSesiones();
      // ✅ ARREGLADO: Llamar callback si existe
      if (onSessionClosed) {
        onSessionClosed();
      }
    } catch (error) {
      console.error('Error cerrando sesión:', error);
      message.error('Error al cerrar la sesión');
    }
  };

  const cerrarSesionesSeleccionadas = async () => {
    try {
      const promises = selectedRowKeys.map(sesionId => 
        api.post(`/api/usuarios-admin/sesiones/${sesionId}/cerrar`)
      );
      
      await Promise.all(promises);
      message.success(`${selectedRowKeys.length} sesiones cerradas exitosamente`);
      setSelectedRowKeys([]);
      cargarSesiones();
      // ✅ ARREGLADO: Llamar callback si existe
      if (onSessionClosed) {
        onSessionClosed();
      }
    } catch (error) {
      console.error('Error cerrando sesiones:', error);
      message.error('Error al cerrar las sesiones seleccionadas');
    }
  };

  const getDeviceIcon = (userAgent) => {
    const ua = userAgent.toLowerCase();
    
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <MobileOutlined style={{ color: '#52c41a' }} />;
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return <TabletOutlined style={{ color: '#1890ff' }} />;
    } else {
      return <LaptopOutlined style={{ color: '#722ed1' }} />;
    }
  };

  const getBrowserInfo = (userAgent) => {
    const ua = userAgent.toLowerCase();
    let browser = 'Desconocido';
    let os = 'Desconocido';

    // Detectar navegador
    if (ua.includes('chrome')) browser = 'Chrome';
    else if (ua.includes('firefox')) browser = 'Firefox';
    else if (ua.includes('safari')) browser = 'Safari';
    else if (ua.includes('edge')) browser = 'Edge';
    else if (ua.includes('opera')) browser = 'Opera';

    // Detectar OS
    if (ua.includes('windows')) os = 'Windows';
    else if (ua.includes('mac')) os = 'macOS';
    else if (ua.includes('linux')) os = 'Linux';
    else if (ua.includes('android')) os = 'Android';
    else if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';

    return { browser, os };
  };

  const getStatusColor = (fechaExpiracion) => {
    const now = dayjs();
    const expira = dayjs(fechaExpiracion);
    const minutosParaExpirar = expira.diff(now, 'minutes');

    if (minutosParaExpirar < 30) return 'red';
    if (minutosParaExpirar < 60) return 'orange';
    return 'green';
  };

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'nombre_persona',
      key: 'usuario',
      width: 150,
      render: (nombre, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{nombre}</div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.usuario}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Dispositivo',
      dataIndex: 'user_agent',
      key: 'dispositivo',
      width: 200,
      render: (userAgent, record) => {
        const { browser, os } = getBrowserInfo(userAgent);
        return (
          <Space>
            {getDeviceIcon(userAgent)}
            <div>
              <div>{browser}</div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {os}
              </Text>
            </div>
          </Space>
        );
      }
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
      render: (ip) => (
        <Text code style={{ fontSize: '12px' }}>
          {ip}
        </Text>
      )
    },
    {
      title: 'Inicio de Sesión',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
      width: 160,
      render: (fecha) => (
        <div>
          <div>{dayjs(fecha).format('DD/MM/YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {dayjs(fecha).format('HH:mm:ss')}
          </Text>
        </div>
      )
    },
    {
      title: 'Última Actividad',
      dataIndex: 'ultimo_acceso',
      key: 'ultimo_acceso',
      width: 140,
      render: (fecha) => (
        <Tooltip title={dayjs(fecha).format('DD/MM/YYYY HH:mm:ss')}>
          <Text>{dayjs(fecha).fromNow()}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Expira',
      dataIndex: 'fecha_expiracion',
      key: 'fecha_expiracion',
      width: 120,
      render: (fecha) => {
        const color = getStatusColor(fecha);
        const minutosParaExpirar = dayjs(fecha).diff(dayjs(), 'minutes');
        
        return (
          <Tag color={color} icon={<ClockCircleOutlined />}>
            {minutosParaExpirar > 0 ? `${Math.floor(minutosParaExpirar / 60)}h ${minutosParaExpirar % 60}m` : 'Expirada'}
          </Tag>
        );
      }
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      render: (_, record) => (
        <Tag color="green">
          Activa
        </Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="¿Cerrar esta sesión?"
          description="El usuario será desconectado inmediatamente"
          onConfirm={() => cerrarSesion(record.id)}
          okText="Sí, cerrar"
          cancelText="Cancelar"
          icon={<WarningOutlined style={{ color: 'red' }} />}
        >
          <Tooltip title="Cerrar sesión">
            <Button
              type="link"
              icon={<DisconnectOutlined />}
              danger
            />
          </Tooltip>
        </Popconfirm>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const sesionesActivas = sesiones.filter(s => s.activa !== false);
  const sesionesInactivas = sesiones.filter(s => s.activa === false);

  return (
    <Modal
      title={
        <Space>
          <GlobalOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Sesiones Activas
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel} // ✅ ARREGLADO: Usar onCancel en lugar de onClose
      width={1400}
      footer={[
        <Button key="close" onClick={onCancel}> {/* ✅ ARREGLADO: Usar onCancel */}
          Cerrar
        </Button>
      ]}
      destroyOnClose
      // ✅ ARREGLADO: Agregar estas props para evitar bloqueos
      maskClosable={true}
      keyboard={true}
    >
      {/* Estadísticas */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                {sesionesActivas.length}
              </Title>
              <Text type="secondary">Sesiones Activas</Text>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: 0, color: '#ff4d4f' }}>
                {sesionesInactivas.length}
              </Title>
              <Text type="secondary">Sesiones Cerradas</Text>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                {new Set(sesiones.map(s => s.usuario_id)).size}
              </Title>
              <Text type="secondary">Usuarios Únicos</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Alerta de sesiones por expirar */}
      {sesionesActivas.some(s => getStatusColor(s.fecha_expiracion) === 'red') && (
        <Alert
          message="Sesiones por expirar"
          description="Hay sesiones que expirarán en menos de 30 minutos"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filtros */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Buscar por IP o User Agent..."
              prefix={<SearchOutlined />}
              value={filtros.search}
              onChange={(e) => handleFiltroChange('search', e.target.value)}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filtrar por usuario"
              value={filtros.usuario_id}
              onChange={(value) => handleFiltroChange('usuario_id', value)}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {usuarios.map(usuario => (
                <Option key={usuario.id} value={usuario.id}>
                  {usuario.nombre_persona} ({usuario.usuario})
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={24} md={8}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={cargarSesiones}
                loading={loading}
              >
                Recargar
              </Button>
              
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`¿Cerrar ${selectedRowKeys.length} sesiones?`}
                  description="Los usuarios seleccionados serán desconectados inmediatamente"
                  onConfirm={cerrarSesionesSeleccionadas}
                  okText="Sí, cerrar todas"
                  cancelText="Cancelar"
                  icon={<WarningOutlined style={{ color: 'red' }} />}
                >
                  <Button 
                    danger 
                    icon={<DisconnectOutlined />}
                  >
                    Cerrar Seleccionadas ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={sesiones}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} de ${total} sesiones`
        }}
        scroll={{ x: 1200 }}
        size="middle"
      />
    </Modal>
  );
};

export default SesionesActivasModal;